import { debounce } from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { identity, isNullish } from 'remeda';
import { FormAutocomplete, FormTextField } from 'src/components';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import {
  ConversationType,
  conversationTypeOptions,
  PreparedConversationParticipantOption,
  useConversationAssigneeGroupsOptions,
  useConversationParticipantOptions,
  useConversationUrgencyOptions,
  usePartnersOptions,
} from 'src/graphql';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { ConditionInput, EntityNames } from 'src/graphql/types';
import { RequesterListItem } from 'src/pages/Conversations/SidePanel/ConversationDetails/requester-list-item';
import { useGlobalStore } from 'src/store';
import { useScrollPaginateHandler } from 'src/utils/hooks';
import { sortAlphabetically } from 'src/utils/sorting';

export enum ConversationFormFields {
  Partner = 'partner',
  Group = 'group',
  Requester = 'requester',
  Site = 'address',
  ConversationType = 'conversationType',
  Urgency = 'urgency',
  Subject = 'subject',
}

export interface NewConversationFormValues {
  [ConversationFormFields.Partner]: {
    id: number;
    label: string;
    supportEmail: string;
    membershipEmail: string;
  } | null;
  [ConversationFormFields.Group]: { id: string; label: string } | null;
  [ConversationFormFields.Requester]: {
    id: string;
    label: string;
    participantEntity: ConversationParticipantEntity;
    email?: string;
    data: unknown;
  } | null;
  [ConversationFormFields.Site]: { id: string; label: string } | null;
  [ConversationFormFields.ConversationType]: { id: ConversationType; label: string } | null;
  [ConversationFormFields.Urgency]: { id: string; label: string } | null;
  [ConversationFormFields.Subject]: string;
}

export function useNewConversationForm({
  disabled = false,
  formMethods,
  addNewSite,
  addNewClient,
}: {
  disabled?: boolean;
  formMethods: ReturnType<typeof useForm<NewConversationFormValues>>;
  addNewSite: (client: PreparedConversationParticipantOption) => void;
  addNewClient: (partnerId: number) => void;
}) {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const partnerOptions = usePartnersOptions();

  const { setValue, watch } = formMethods;

  const requester = watch(ConversationFormFields.Requester);
  const partner = watch(ConversationFormFields.Partner);

  const [selectedPartner, setSelectedPartner] = useState(() => {
    if (!isNullish(selectedPartnerId)) {
      return partnerOptions.find((p) => p.id === selectedPartnerId);
    }

    if (partner) {
      return partner;
    }

    return partnerOptions.length === 1 ? partnerOptions[0] : null;
  });
  useEffect(() => {
    if (!isNullish(selectedPartnerId)) {
      setSelectedPartner(partnerOptions.find((p) => p.id === selectedPartnerId));
    } else if (partner) {
      setSelectedPartner(partner);
    } else {
      setSelectedPartner(partnerOptions.length === 1 ? partnerOptions[0] : null);
    }
  }, [partnerOptions, selectedPartnerId, partner]);

  const conversationAssigneeGroupOptions = useConversationAssigneeGroupsOptions(
    selectedPartner?.id,
  );

  const [searchRequesterTerm, setSearchRequesterTerm] = useState('');

  const {
    getConversationParticipantOptions,
    conversationParticipantOptions,
    loading: requesterLoading,
  } = useConversationParticipantOptions();

  const requesterOptions = useMemo(() => {
    return conversationParticipantOptions
      .filter((it) =>
        [
          ConversationParticipantEntity.Client,
          ConversationParticipantEntity.PlatformUser,
        ].includes(it.participantEntity),
      )
      .map((option) => {
        return {
          ...option,
          data: option,
          label: option.label || option.email,
        };
      });
  }, [conversationParticipantOptions]);

  useEffect(() => {
    if (selectedPartner?.id) {
      getConversationParticipantOptions(
        {
          partnerId: selectedPartner?.id,
          searchText: searchRequesterTerm,
        },
        true,
      );
    }
  }, [selectedPartner?.id, searchRequesterTerm, getConversationParticipantOptions]);

  const [searchAddressTerm, setSearchAddressTerm] = useState('');
  const addressFilters = useMemo(() => {
    const filters: ConditionInput[] = [];

    if (!isNullish(selectedPartner?.id ?? selectedPartnerId)) {
      filters.push({
        attribute: 'partnerId',
        operator: '=',
        value: String(selectedPartner?.id ?? selectedPartnerId),
      });
    }

    if (requester?.id) {
      filters.push({
        attribute: 'clients/clientId',
        operator: '=',
        value: requester?.id,
      });
    }

    return filters;
  }, [selectedPartner?.id, selectedPartnerId, requester?.id]);

  const {
    records: addresses,
    isLoading: addressLoading,
    loadMore: addressLoadMore,
    hasMore: addressHasMore,
  } = useSearch({
    entityName: EntityNames.Site,
    searchTerm: searchAddressTerm,
    filter: addressFilters,
    sort: { attribute: 'address1', desc: false },
    showResultsWhenEmptySearchTerm: true,
    skip: isNullish(requester?.id),
  }) ?? {};

  const addressHandleScroll = useScrollPaginateHandler({
    loadMore: addressLoadMore,
    enabled: addressHasMore,
    thresholdPx: 100,
  });

  const addressOptions = useMemo(() => {
    if (!addresses) return [];
    return (
      addresses
        // Remove duplicated elements
        ?.filter(
          (address, index, arr) =>
            arr.findIndex((a) => a.addressId === address.addressId) === index,
        )
        .map((address) => ({
          id: address.addressId,
          label: address.fullAddress ?? '',
        }))
        .sort(sortAlphabetically('label'))
    );
  }, [addresses]);

  const conversationUrgencyOptions = useConversationUrgencyOptions(selectedPartner?.id);

  const clearFields = useCallback(() => {
    setSearchRequesterTerm('');
    setSearchAddressTerm('');
  }, []);

  const fieldCommonProps = useMemo(() => {
    return {
      disabled,
      underlined: true,
    };
  }, [disabled]);

  const formFields = useMemo(() => {
    const hasSinglePartner = partnerOptions.length === 1;
    const shouldHidePartnerSelection = hasSinglePartner || !isNullish(selectedPartnerId);

    return (
      <>
        {!shouldHidePartnerSelection && (
          <FormAutocomplete
            {...fieldCommonProps}
            type="autocomplete"
            label="Partner"
            options={partnerOptions}
            name={ConversationFormFields.Partner}
            required="Partner is required"
            blurOnSelect={false}
            autocompleteProps={{
              onChange: (_, value) => {
                const partner = (
                  Array.isArray(value) ? value[0] : value
                ) as NewConversationFormValues['partner'];
                clearFields();
                setSelectedPartner(partner);

                setValue(ConversationFormFields.Partner, partner);
                setValue(ConversationFormFields.Group, null);
                setValue(ConversationFormFields.Requester, null);
                setValue(ConversationFormFields.Site, null);
                setValue(ConversationFormFields.ConversationType, null);
                setValue(ConversationFormFields.Urgency, null);
              },
            }}
          />
        )}
        {!!partner && (
          <FormAutocomplete
            {...fieldCommonProps}
            type="autocomplete"
            label="Group"
            options={conversationAssigneeGroupOptions}
            name={ConversationFormFields.Group}
            required="Group is required"
            blurOnSelect={false}
          />
        )}
        {!!partner && (
          <FormAutocomplete
            {...fieldCommonProps}
            type="autocomplete"
            name={ConversationFormFields.Requester}
            label="Requester"
            required="Requester is required"
            options={requesterOptions}
            creatable
            loading={requesterLoading}
            onAddNewItemLabel="+ Create Client"
            blurOnSelect={false}
            onAddNewItem={() => {
              if (!isNullish(selectedPartner?.id)) {
                addNewClient(selectedPartner.id);
              }
            }}
            autocompleteProps={{
              onChange: () => {
                clearFields();
                setValue(ConversationFormFields.Site, null);
              },
              groupBy: (option) => option.group,
              loading: requesterLoading,
              ListboxProps: {
                sx: {
                  maxHeight: '18rem',
                },
              },
              componentsProps: {
                paper: {
                  sx: {
                    // makes the group labels to have rounded corners too
                    overflow: 'hidden',
                  },
                },
              },
              filterOptions: identity,

              renderOption: (props, option: PreparedConversationParticipantOption, state) => (
                <RequesterListItem
                  id={option.id}
                  props={props}
                  key={option.id}
                  inputValue={state.inputValue}
                  firstLine={option.label}
                  secondLine={option.email}
                  participantEntity={option.participantEntity}
                  externalPartnerId={
                    option.entityPartnerId !== partner.id ? option.entityPartnerId : undefined
                  }
                />
              ),
            }}
            textFieldProps={{
              onChange: debounce(
                ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  setSearchRequesterTerm(target.value);
                },
                SEARCH_DEBOUNCE_DELAY,
              ),
            }}
          />
        )}
        {!!partner &&
          !!requester &&
          requester.participantEntity === ConversationParticipantEntity.Client && (
            <FormAutocomplete
              {...fieldCommonProps}
              type="autocomplete"
              creatable
              name={ConversationFormFields.Site}
              label="Site"
              options={addressOptions}
              blurOnSelect={false}
              onAddNewItem={() => {
                if (requester?.data) {
                  addNewSite(requester.data as PreparedConversationParticipantOption);
                }
              }}
              onAddNewItemLabel="+ Create New Site"
              inputProps={{
                value: '',
              }}
              autocompleteProps={{
                loading: addressLoading,
                ListboxProps: {
                  onScroll: addressHandleScroll,
                  sx: {
                    maxHeight: '18rem',
                  },
                },
                filterOptions: identity,
                onClose: () => {
                  setSearchAddressTerm('');
                },
              }}
              textFieldProps={{
                onChange: debounce(
                  ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setSearchAddressTerm(target.value);
                  },
                  SEARCH_DEBOUNCE_DELAY,
                ),
              }}
            />
          )}
        <FormAutocomplete
          {...fieldCommonProps}
          type="autocomplete"
          name={ConversationFormFields.ConversationType}
          label="Type"
          required="Type is required"
          options={conversationTypeOptions}
          blurOnSelect={false}
        />
        <FormAutocomplete
          {...fieldCommonProps}
          type="autocomplete"
          name={ConversationFormFields.Urgency}
          label="Urgency"
          required="Urgency is required"
          options={conversationUrgencyOptions}
          blurOnSelect={false}
        />
        <FormTextField
          {...fieldCommonProps}
          type="textfield"
          name={ConversationFormFields.Subject}
          label="Subject"
          required="Subject is required"
          textFieldProps={{
            inputProps: {
              maxLength: 100,
            },
          }}
        />
      </>
    );
  }, [
    partnerOptions,
    selectedPartnerId,
    fieldCommonProps,
    partner,
    conversationAssigneeGroupOptions,
    requesterOptions,
    requesterLoading,
    requester,
    addressOptions,
    addressLoading,
    addressHandleScroll,
    conversationUrgencyOptions,
    clearFields,
    setValue,
    selectedPartner?.id,
    addNewClient,
    addNewSite,
  ]);

  return { formFields };
}
